import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import deepmerge from 'deepmerge';
import { Box, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';

import PriceListProductFilters from './filters';
import { getPriceListProductsAction, getPriceListProductsState } from '../../../../features/product/get_price_list_products';
import { getConstantsNotificationData, getPriceListProductsNotificationData } from '../../../../core/utils/notification_utils';
import { getDefaultProductPaginationQueryValues } from '../../../../core/utils/api_utils';
import useNotification from '../../../../core/hooks/use_notification';
import PaginationControls from '../../../common/pagination_controls';
import PriceListPriceInput from '../../common/price_list_price_input';
import BlockIcon from '@mui/icons-material/Block';
import { getConstantsAction, getConstantsState, invalidateConstants } from '../../../../features/product/get_constants';

const initialQueryParams = {
    ...getDefaultProductPaginationQueryValues,
    sort: ['workingName', 'asc'],
    query: '',
    productTypeId: '',
};

const ProductsList = ({ priceListId }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const [queryParams, setQueryParams] = useState({ ...initialQueryParams, priceListId });

    const getPriceListProducts = useSelector(getPriceListProductsState);
    const getConstants = useSelector(getConstantsState);

    const refreshProductList = useCallback(() => {
        dispatch(getPriceListProductsAction(queryParams))
            .then(response => showErrorMessage(getPriceListProductsNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshProductList();
    }, [refreshProductList]);

    useEffect(() => {
        dispatch(getConstantsAction())
            .then(response => showErrorMessage(getConstantsNotificationData(response)));

        return () => {
            dispatch(invalidateConstants());
        };
    }, [dispatch, showErrorMessage]);

    return (
        <Box>
            <Box mb={ 4 }>
                {
                    getConstants?.data &&
                    <PriceListProductFilters
                        queryParams={ queryParams }
                        onSearchInputChange={ query => setQueryParams(prevState => deepmerge(prevState, { query })) }
                        onProductTypeChange={ productTypeId => setQueryParams(prevState => deepmerge(prevState, { productTypeId })) }/>
                }
            </Box>

            { getPriceListProducts?.loading && (
                <Box mb={ 2 }>
                    <LinearProgress/>
                </Box>
            ) }

            { getPriceListProducts?.data?.content && (
                <Box>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell sx={ { width: 100 } }>ID</TableCell>
                                    <TableCell>Nazwa</TableCell>
                                    <TableCell sx={ { width: 200 } } align="center">Cena</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    getPriceListProducts?.data?.content?.map(priceListProduct => (
                                        <TableRow key={ priceListProduct?.id } hover>
                                            <TableCell sx={ { fontWeight: 600 } }>
                                                { priceListProduct?.id }
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={ 1 }>
                                                    <Typography
                                                        variant="body2"
                                                        color={ priceListProduct?.active === false ? 'text.secondary' : 'text.primary' }
                                                    >
                                                        { priceListProduct?.name }
                                                    </Typography>

                                                    { priceListProduct?.active === false && (
                                                        <Tooltip title="Produkt jest nieaktywny" placement="top">
                                                            <BlockIcon fontSize="small" color="disabled"/>
                                                        </Tooltip>
                                                    ) }
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <PriceListPriceInput
                                                    productId={ priceListProduct?.id }
                                                    initialPrice={ priceListProduct?.priceListPrice }
                                                    priceListId={ priceListId }
                                                    refreshList={ refreshProductList }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box mt={ 3 } display="flex" justifyContent="center">
                        <PaginationControls
                            currentPage={ getPriceListProducts?.data?.pageable?.pageNumber }
                            totalPages={ getPriceListProducts?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default ProductsList;